import { apiPost } from '../api.js';
import { t } from '../i18n.js';
import { toast } from './toast.js';
import { bottomSheet } from './bottom-sheet.js';
import { chevronDown } from './icons.js';
import { iconPicker, fetchPalette } from './icon-picker.js';
import { unlockTimerAudio, CHIME_IDS, DEFAULT_CHIME } from './timer-alarm.js';

export { CHIME_IDS, DEFAULT_CHIME };

// ---------------------------------------------------------------------------
// Pure duration helpers — exported for direct unit testing (no DOM needed).
// MAX_DURATION_MS mirrors the server's 7-day ceiling (422 invalid_duration),
// which itself matches the recipe step-wait cap (see spec FR-RCP-13).
// ---------------------------------------------------------------------------

export const PRESET_MINUTES = [5, 10, 15, 20, 30, 60];
export const MAX_DURATION_MS = 604_800_000;

/**
 * The label for a preset's minutes value: hour-aware once it reaches 60 min
 * (design Decision E — the 1h preset must read "1 h", not "60 min").
 *
 * @param {number} minutes
 * @returns {string}
 */
export function presetLabel(minutes) {
  if (minutes >= 60 && minutes % 60 === 0) {
    return t('timers.preset_hours', { hours: minutes / 60 });
  }
  return t('timers.preset_minutes', { minutes });
}

/**
 * @param {number} ms
 * @returns {boolean}
 */
export function isValidDurationMs(ms) {
  return Number.isFinite(ms) && ms > 0 && ms <= MAX_DURATION_MS;
}

/**
 * Converts a custom D:H:M:S entry to milliseconds. Non-finite/blank fields are
 * treated as 0 so a partially-filled form still computes a definite value.
 *
 * @param {{days?: number|string, hours?: number|string, minutes?: number|string, seconds?: number|string}} parts
 * @returns {number}
 */
export function customDurationMs({ days = 0, hours = 0, minutes = 0, seconds = 0 } = {}) {
  const safe = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  };
  const totalSeconds = (safe(days) * 86400) + (safe(hours) * 3600) + (safe(minutes) * 60) + safe(seconds);
  return Math.round(totalSeconds * 1000);
}

function numberField(role, labelText) {
  const wrap = document.createElement('div');
  wrap.className = 'space-y-1';

  const lbl = document.createElement('label');
  lbl.className = 'label block text-xs';
  lbl.textContent = labelText;

  const input = document.createElement('input');
  input.type = 'number';
  input.min = '0';
  input.step = '1';
  input.inputMode = 'numeric';
  input.value = '0';
  input.className = 'input w-20';
  input.dataset.role = role;

  wrap.append(lbl, input);
  return { wrap, input };
}

/**
 * Timer create form: general settings (label, symbol, chime) first, followed
 * by the duration group — quick presets (5/10/15/20/30 min + 1 h) and an
 * inline custom D:H:M:S entry, sharing a single section divider. Each preset
 * button and the custom submit button POST directly (matching the page's
 * create-form seam) and hand the created timer to `onCreated`.
 *
 * @param {{onCreated: (timer: object) => void, onQueued?: (key: string) => void}} opts
 *   `onQueued` receives the outbox key when the create was queued offline —
 *   the envelope is never passed to `onCreated` (IG-R10).
 * @returns {{el: HTMLElement}}
 */
export function timerCreateForm({ onCreated, onQueued = null }) {
  const el = document.createElement('div');
  el.className = 'card space-y-4';
  el.dataset.role = 'timer-create-form';

  const titleEl = document.createElement('h2');
  titleEl.className = 'font-semibold text-base';
  titleEl.textContent = t('timers.create_title');
  el.append(titleEl);

  // Section grouping (visual only — one screen, one Save): "Label & symbol"
  // covers naming/appearance/chime; "Duration" covers presets + custom entry.
  const labelSectionLabel = document.createElement('p');
  labelSectionLabel.className = 'section-label';
  labelSectionLabel.textContent = t('timers.section_label_symbol');
  el.append(labelSectionLabel);

  // --- Label (shared by presets and custom entry) ---
  const labelWrap = document.createElement('div');
  labelWrap.className = 'space-y-1';
  const labelLabel = document.createElement('label');
  labelLabel.className = 'label block';
  labelLabel.textContent = t('timers.label_label');
  const labelInput = document.createElement('input');
  labelInput.type = 'text';
  labelInput.maxLength = 120;
  labelInput.className = 'input w-full';
  labelInput.placeholder = t('timers.label_placeholder');
  labelInput.dataset.role = 'timer-create-label';
  labelWrap.append(labelLabel, labelInput);
  el.append(labelWrap);

  // --- Optional symbol (shared icon picker — Decision F) ---
  const symbolWrap = document.createElement('div');
  symbolWrap.className = 'space-y-1';
  symbolWrap.dataset.role = 'timer-create-symbol';
  const symbolLabelEl = document.createElement('label');
  symbolLabelEl.className = 'label block';
  symbolLabelEl.textContent = t('timers.symbol_label');
  symbolWrap.append(symbolLabelEl);

  const symbolPlaceholderEl = document.createElement('div');
  symbolPlaceholderEl.className = 'text-xs text-ink-muted';
  symbolPlaceholderEl.textContent = '…';
  symbolWrap.append(symbolPlaceholderEl);
  el.append(symbolWrap);

  let symbolPicker = { getValue: () => null };
  fetchPalette().then((palette) => {
    symbolPicker = iconPicker(null, palette);
    symbolPlaceholderEl.replaceWith(symbolPicker.el);
  }).catch(() => { /* silent — leaves the symbol field as a no-op */ });

  // --- Optional chime (single-select from a bounded list of six — IG-R8).
  // Presented the same way as the symbol field directly above it: a compact
  // `.picker-field` summary row that opens one shared surface. The surface is
  // `bottomSheet({ kind: 'radio' })`, so the selected-state glyph comes from
  // the shared component rather than being hand-typed into a label. It used to
  // be an inline radio list, which made two adjacent fields in the same form
  // speak two different interaction languages. ---
  const chimeWrap = document.createElement('div');
  chimeWrap.className = 'space-y-1';
  chimeWrap.dataset.role = 'timer-create-chime';
  const chimeLabelEl = document.createElement('p');
  chimeLabelEl.className = 'label';
  chimeLabelEl.textContent = t('timers.chime_label');
  chimeWrap.append(chimeLabelEl);

  let selectedChime = DEFAULT_CHIME;
  let previewAudio = null;

  function playChimePreview(chimeId) {
    unlockTimerAudio();
    if (previewAudio) {
      previewAudio.pause();
      previewAudio.src = '';
      previewAudio = null;
    }
    try {
      const audio = new Audio(`/chimes/${chimeId}.ogg`);
      previewAudio = audio;
      const playResult = audio.play();
      if (playResult && typeof playResult.catch === 'function') {
        playResult.catch(() => { /* autoplay blocked / decode failure — silent */ });
      }
    } catch { /* Audio unavailable — silent, preview is best-effort */ }
  }

  const chimeRow = document.createElement('div');
  chimeRow.className = 'flex items-center gap-2';

  const chimeField = document.createElement('button');
  chimeField.type = 'button';
  chimeField.className = 'picker-field flex-1 min-w-0';
  chimeField.dataset.role = 'timer-create-chime-field';

  const chimeNameEl = document.createElement('span');
  chimeNameEl.className = 'text-sm font-medium truncate';

  const chimeHint = document.createElement('span');
  chimeHint.className = 'picker-field__hint inline-flex items-center gap-0.5';
  const chimeHintText = document.createElement('span');
  chimeHintText.textContent = t('timers.chime_change');
  chimeHint.append(chimeHintText, chevronDown('-rotate-90 w-3.5 h-3.5 opacity-70'));

  chimeField.append(chimeNameEl, chimeHint);

  // Preview stays inline next to the field so the current choice can be heard
  // without reopening the surface; picking a row in the surface also plays it.
  const chimePreviewBtn = document.createElement('button');
  chimePreviewBtn.type = 'button';
  chimePreviewBtn.className = 'btn text-sm shrink-0';
  chimePreviewBtn.dataset.role = 'timer-create-chime-preview';
  chimePreviewBtn.textContent = '▶';
  chimePreviewBtn.addEventListener('click', () => playChimePreview(selectedChime));

  function renderChimeField() {
    const name = t(`timers.chime_${selectedChime}`);
    chimeNameEl.textContent = name;
    chimeField.setAttribute('aria-label', `${t('timers.chime_label')}: ${name}`);
    chimePreviewBtn.dataset.chime = selectedChime;
    chimePreviewBtn.setAttribute('aria-label', t('timers.chime_preview', { chime: name }));
  }

  chimeField.addEventListener('click', () => {
    bottomSheet({
      title: t('timers.chime_label'),
      rows: CHIME_IDS.map((chimeId) => ({
        kind: 'radio',
        label: t(`timers.chime_${chimeId}`),
        selected: chimeId === selectedChime,
        onChange: () => {
          selectedChime = chimeId;
          renderChimeField();
          playChimePreview(chimeId);
        },
      })),
    });
  });

  renderChimeField();
  chimeRow.append(chimeField, chimePreviewBtn);
  chimeWrap.append(chimeRow);
  el.append(chimeWrap);

  // --- Duration group: presets + custom D:H:M:S entry, one visual block ---

  const durationSectionLabel = document.createElement('p');
  durationSectionLabel.className = 'section-label';
  durationSectionLabel.textContent = t('timers.section_duration');
  el.append(durationSectionLabel);

  // Presets
  const presetsRow = document.createElement('div');
  presetsRow.className = 'flex flex-wrap gap-2 pt-3 border-t border-border dark:border-border-dark';
  presetsRow.dataset.role = 'timer-create-presets';

  for (const minutes of PRESET_MINUTES) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'btn text-sm';
    btn.dataset.role = 'timer-create-preset';
    btn.dataset.minutes = String(minutes);
    btn.textContent = presetLabel(minutes);
    btn.addEventListener('click', () => submitDuration(minutes * 60000));
    presetsRow.append(btn);
  }
  el.append(presetsRow);

  // Custom D:H:M:S entry (inline, always visible — no bottom sheet needed)
  const customWrap = document.createElement('div');
  customWrap.className = 'space-y-2';
  customWrap.dataset.role = 'timer-create-custom';

  const customLabelEl = document.createElement('p');
  customLabelEl.className = 'label';
  customLabelEl.textContent = t('timers.custom_duration_label');
  customWrap.append(customLabelEl);

  const customRow = document.createElement('div');
  customRow.className = 'flex gap-2 items-end flex-wrap';

  const daysField = numberField('timer-create-days', t('timers.custom_days_label'));
  const hoursField = numberField('timer-create-hours', t('timers.custom_hours_label'));
  const minutesField = numberField('timer-create-custom-minutes', t('timers.minutes_label'));
  const secondsField = numberField('timer-create-seconds', t('timers.custom_seconds_label'));

  const customSubmitBtn = document.createElement('button');
  customSubmitBtn.type = 'button';
  customSubmitBtn.className = 'btn-primary text-sm';
  customSubmitBtn.dataset.role = 'timer-create-custom-submit';
  customSubmitBtn.textContent = t('timers.start_button');
  customSubmitBtn.addEventListener('click', () => {
    const ms = customDurationMs({
      days: daysField.input.value,
      hours: hoursField.input.value,
      minutes: minutesField.input.value,
      seconds: secondsField.input.value,
    });
    submitDuration(ms);
  });

  customRow.append(daysField.wrap, hoursField.wrap, minutesField.wrap, secondsField.wrap, customSubmitBtn);
  customWrap.append(customRow);
  el.append(customWrap);

  const errorEl = document.createElement('p');
  errorEl.className = 'text-sm text-danger hidden';
  errorEl.dataset.role = 'timer-create-error';
  el.append(errorEl);

  function setBusy(busy) {
    for (const btn of el.querySelectorAll('button')) btn.disabled = busy;
  }

  function resetInputs() {
    labelInput.value = '';
    daysField.input.value = '0';
    hoursField.input.value = '0';
    minutesField.input.value = '0';
    secondsField.input.value = '0';
  }

  async function submitDuration(durationMs) {
    errorEl.classList.add('hidden');
    errorEl.textContent = '';

    if (!isValidDurationMs(durationMs)) {
      errorEl.textContent = t('errors.invalid_duration');
      errorEl.classList.remove('hidden');
      return;
    }

    const label = labelInput.value.trim();
    const body = { duration_ms: durationMs };
    if (label) body.label = label;

    const symbol = symbolPicker.getValue();
    if (symbol) body.symbol = symbol;

    if (selectedChime !== DEFAULT_CHIME) body.chime = selectedChime;

    setBusy(true);
    try {
      const created = await apiPost('/timers', body);
      resetInputs();
      // `POST /timers` is outbox-allowlisted: offline it answers with a
      // `{queued:true,key}` envelope, which is not a timer and must never be
      // handed to the page's list (IG-R10). The page renders the shared
      // pending indication for the key instead.
      if (created && created.queued === true) {
        if (onQueued) onQueued(created.key || `pending-create-${Date.now()}`);
        toast.show(t('inventory.pending_badge'), 'info');
        return;
      }
      onCreated(created);
      toast.show(t('timers.toast_started'), 'success');
    } catch (err) {
      if (err && err.status === 422) {
        errorEl.textContent = t('errors.invalid_duration');
        errorEl.classList.remove('hidden');
      } else {
        const isNetwork = err && err.code === 'network';
        toast.show(isNetwork ? t('errors.network') : t('errors.server'), 'error');
      }
    } finally {
      setBusy(false);
    }
  }

  return { el };
}
